import { Theme } from '@mui/material';
import styled from '@emotion/styled';
import { DataContext } from 'providers/data';
import { useContext, useEffect, useState } from 'react';
import { collection, query, getDocs } from "firebase/firestore";

import { DbGet } from 'config/firebase';

import { Box, Typography } from '@mui/material';
import NavTitle from 'components/views/NavTitle';
import HeaderSubPages from 'components/views/MyVehicles/HeaderSubPages';

import { ReactComponent as IcCoverageCheck } from 'assets/icons/ic-coverage-check.svg';
import { ReactComponent as IcIturinCoverage } from 'assets/icons/ic-iturin-coverage.svg';
import { ReactComponent as IcCoverageNoCheck } from 'assets/icons/ic-coverage-no-check.svg';

type ParamsStyle = {
    theme?: Theme;
}

type ParamsProductPlane = {
    CD_PRODUTO: number;
    DS_PRODUTO: Array<ParamsCoverageProduct>;
    NM_PRODUTO: string;
    NR_TIPO_PRODUTO: number;
}

type ParamsCoverageProduct = {
    CD_PRODUTO_ADD: number;
    NM_PRODUTO_ADD: string;
};

type ParamsCoverageContemplated = {
    contemplated: boolean;
    NM_PRODUTO_ADD: string;
};

export const BoxCustom = styled(Box)(
    ({ theme }: ParamsStyle) => `
    display: flex;
    gap: 1.1875rem;
    flex-direction: column;

    a, p {
        display: flex;
        font-weight: 700;
        border-radius: 5px;
        aling-items: center;
        font-size: 1.5625rem;
        color: ${theme?.palette.primary.main};
        border: 2px solid #8D9BB5;
        justify-content: space-between;
        padding: .875rem 1rem .62rem;

        svg {
            width: 25px;
            height: auto;
        }
    }

    p {
        font-size: 1rem;
        font-weight: 600;
    }

    @media(min-width: ${theme?.breakpoints.values.md}px) {
        p {
            font-size: 1.5625rem;
            font-weight: 700;
        }

        a, p {
            padding: .75rem 1.125rem .55rem;
        }
    }
`);

const CoveragePlane = () => {
    const [productSelected, setProductSelected] = useState<ParamsProductPlane>();
    const [coveragesTracker, setCoveragesTracker] = useState<Array<ParamsCoverageProduct>>();
    const [contemplatedCoverages, setContemplatedCoverages] = useState<Array<ParamsCoverageContemplated>>();

    const { selectedCar } = useContext(DataContext);

    const getProductsTemplate = async (CD_PRODUTO_SELECTED: number, NR_TIPO_PRODUTO: number, NM_PRODUTO_SELECTED: string) => {
        let data: { ics: Array<ParamsProductPlane>; rastreadores: Array<ParamsProductPlane> } = { ics: [], rastreadores: [] };

        const products = query(collection(DbGet, "planos"));
        const querySnapshot = await getDocs(products);

        querySnapshot.forEach((doc) => {
            // @ts-ignore
            data = doc.data();
        });

        if (Array.isArray(data?.ics) || Array.isArray(data?.rastreadores)) {
            if (NR_TIPO_PRODUTO === 12) {
                let product = data?.ics.find(({ CD_PRODUTO }) => CD_PRODUTO === CD_PRODUTO_SELECTED);

                if (!product) {
                    product = data?.ics.find(({ NM_PRODUTO }) => NM_PRODUTO === NM_PRODUTO_SELECTED);
                }

                product && setProductSelected(product);
            } else {
                let product = data?.rastreadores.find(({ CD_PRODUTO }) => CD_PRODUTO === CD_PRODUTO_SELECTED);

                if (!product) {
                    product = data?.ics.find(({ NM_PRODUTO }) => NM_PRODUTO === NM_PRODUTO_SELECTED);
                }

                product && setProductSelected(product);
            }
        }
    }

    const getCoverageTemplate = async (NR_TIPO_PRODUTO: number) => {
        let data: { ics: Array<ParamsCoverageProduct>; rastreadores: Array<ParamsCoverageProduct> } = { ics: [], rastreadores: [] };

        const coverages = query(collection(DbGet, "coberturas"));
        const querySnapshot = await getDocs(coverages);

        querySnapshot.forEach((doc) => {
            // @ts-ignore
            data = doc.data();
        });

        if (Array.isArray(data?.ics) || Array.isArray(data?.rastreadores)) {
            if (NR_TIPO_PRODUTO === 12) {
                setCoveragesTracker(data?.ics);
            } else {
                setCoveragesTracker(data?.rastreadores);
            }
        }
    }

    useEffect(() => {
        if (selectedCar) {
            getCoverageTemplate(selectedCar.NR_TIPO_PRODUTO);
            getProductsTemplate(selectedCar.CD_PRODUTO, selectedCar.NR_TIPO_PRODUTO, selectedCar.PRODUTO);
        }
    }, [selectedCar]);

    useEffect(() => {
        if (productSelected && coveragesTracker && selectedCar) {
            const coveragesContemplated: Array<ParamsCoverageContemplated> = [];

            // eslint-disable-next-line array-callback-return
            coveragesTracker?.map(({ CD_PRODUTO_ADD, NM_PRODUTO_ADD }) => {
                if ((CD_PRODUTO_ADD === 2) && (selectedCar?.NR_PORCENTAGEM_INDENIZACAO === 100)) {
                    coveragesContemplated.push({ NM_PRODUTO_ADD, contemplated: true });
                } else if (productSelected.DS_PRODUTO.findIndex(({ CD_PRODUTO_ADD: CD_PRODUTO_ADD_ARRAY }) => CD_PRODUTO_ADD_ARRAY === CD_PRODUTO_ADD) > -1) {
                    coveragesContemplated.push({ NM_PRODUTO_ADD, contemplated: true });
                } else {
                    coveragesContemplated.push({ NM_PRODUTO_ADD, contemplated: false });
                }
            });

            setContemplatedCoverages(coveragesContemplated);
        }
    }, [productSelected, coveragesTracker, selectedCar]);

    return (
        <HeaderSubPages>
            <Box sx={{ padding: '.5rem 0 2rem', '#title-nav': { fontSize: '1rem' } }}>
                <NavTitle title='Cobertura' />
                <Typography sx={{
                    fontSize: '1.56rem',
                    color: 'secondary.main',
                    margin: '1.75rem 0 .68rem',
                    fontWeight: 600
                }}>
                    {productSelected?.NM_PRODUTO}
                </Typography>
                <Typography sx={{
                    fontSize: '1rem',
                    marginBottom: '1.8175rem'
                }}>
                    Você possui as coberturas listadas abaixo:
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '3rem', flexDirection: { xs: 'column', md: 'row' } }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
                        {contemplatedCoverages?.map(({ NM_PRODUTO_ADD, contemplated }, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: '.68rem', alignItems: 'center', 'svg': { minWidth: '1rem', minHeight: '1rem' } }}>
                                {contemplated ? <IcCoverageCheck /> : <IcCoverageNoCheck />}
                                <Typography fontSize='1rem' fontWeight={600} paddingTop='4px' color={contemplated ? 'primary.main' : 'grey.400'} dangerouslySetInnerHTML={{ __html: NM_PRODUTO_ADD }}></Typography>
                            </Box>
                        ))}
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                        <IcIturinCoverage />
                        <Box sx={{
                            border: '1.5px solid rgba(10, 168, 227, 0.45)',
                            borderRadius: '10px',
                            maxWidth: '21.875rem',
                            padding: '1.5625rem 1.25rem 1.875rem',
                            marginTop: '-30px',
                            backgroundColor: 'white'
                        }}>
                            <Typography fontSize='1rem' lineHeight='20px' textAlign='center'>
                                <strong>Importante:</strong> A cobertura informada ao lado não altera ou invalida as condições
                                comerciais contratadas que constam em sua apólice.
                                <strong> Sempre verifique as coberturas de sua apólice de seguros.</strong>
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </HeaderSubPages >
    );
}

export default CoveragePlane;